// ProcurementInvoiceQRModal.tsx — "scan to upload a vendor invoice from
// phone" QR flow. Same visual shell/polling pattern as
// components/MobileUploadQRModal.tsx (QR code, countdown, "N received"
// state), kept as its own component rather than reusing that one directly:
// MobileUploadQRModal's own header comment documents it being deliberately
// tied to specific legacy z-index/caller behavior for its three existing
// patient-flow callers, and this flow is branch-scoped (a vendor invoice
// belongs to the receiving branch), not patient-scoped — a different
// session/status contract underneath, even though the UI looks the same.
import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Camera, TriangleAlert, CircleCheck, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createVendorInvoiceMobileSession, getVendorInvoiceMobileSessionStatus } from '../api';
import { loadConfig, getActiveModel } from '@/aiConfig';
import { progressMessage } from './useInvoiceUploadFlow';
import type { VendorInvoiceMobileSession, ExtractedInvoiceLine } from '../types';

// Mirrors the backend's INVOICE_UPLOAD_SESSION_TTL_MS (pharmacy-procurement.js).
const SESSION_TTL_MS = 15 * 60 * 1000;

interface ProcurementInvoiceQRModalProps {
  poId?: number;
  onClose: () => void;
  /** Fired once an uploaded invoice finishes extraction successfully. */
  onReceived: (lines: ExtractedInvoiceLine[]) => void;
}

export default function ProcurementInvoiceQRModal({ poId, onClose, onReceived }: ProcurementInvoiceQRModalProps) {
  const [session, setSession] = useState<VendorInvoiceMobileSession | null>(null);
  const [error, setError] = useState('');
  const [receivedCount, setReceivedCount] = useState(0);
  const [reading, setReading] = useState(false);
  // Set the first time a 'processing' upload is seen for the current invoice
  // read, cleared once it settles — lets the "reading" notice show real
  // elapsed-time-based progress instead of one static message for the whole
  // (possibly minute-long) wait.
  const [readingSince, setReadingSince] = useState<number | null>(null);
  const [failedMessage, setFailedMessage] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expired, setExpired] = useState(false);
  const handledUploadIds = useRef(new Set<number>());
  // Guards React 18 StrictMode's dev-mode double-invoke (mount -> cleanup ->
  // mount on the same instance) from minting two sessions for one QR shown —
  // same guard MobileUploadQRModal.tsx uses.
  const createdRef = useRef(false);

  useEffect(() => {
    if (createdRef.current) return;
    createdRef.current = true;
    // Same AI Settings choice the desk upload sends (src/aiConfig.ts) —
    // captured now since the phone that uploads has no AI config of its own.
    const cfg = loadConfig();
    createVendorInvoiceMobileSession(poId, cfg.provider, getActiveModel(cfg))
      .then((s) => { setSession(s); setSecondsLeft(Math.round(SESSION_TTL_MS / 1000)); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [poId]);

  useEffect(() => {
    if (!session) return;
    const id = setInterval(async () => {
      const left = Math.max(0, Math.round((new Date(session.expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) { setExpired(true); return; }
      try {
        const status = await getVendorInvoiceMobileSessionStatus(session.token);
        if (status.expired) { setExpired(true); return; }
        if (status.uploadCount > receivedCount) setReceivedCount(status.uploadCount);

        for (const upload of status.uploads) {
          if (handledUploadIds.current.has(upload.id)) continue;
          if (upload.status === 'processing') {
            setReading(true);
            setReadingSince((prev) => prev ?? Date.now());
            continue;
          }
          handledUploadIds.current.add(upload.id);
          setReading(false);
          setReadingSince(null);
          if (upload.status === 'done' && upload.extracted_data) onReceived(upload.extracted_data);
          else if (upload.status === 'failed') setFailedMessage(upload.error_message || 'Could not read that invoice automatically.');
        }
      } catch { /* transient poll failure — try again next tick */ }
    }, 3000);
    return () => clearInterval(id);
  }, [session, receivedCount, onReceived]);

  const mobileUrl = session?.uploadUrl || '';
  const usingLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)([:/]|$)/i.test(mobileUrl);
  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  const runningOut = !expired && secondsLeft > 0 && secondsLeft < 120;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" onClick={onClose} role="presentation">
      <div
        className="w-full max-w-[360px] rounded-xl border border-border bg-card p-6 text-center shadow-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Scan to upload vendor invoice from phone"
      >
        <div className="mb-1 flex items-center justify-center gap-2 text-[15px] font-bold">
          <Camera size={17} /> Scan to upload invoice
        </div>
        <div className="mb-4 text-[12px] text-ink-muted">Vendor invoice</div>

        {error && <div className="mb-3 rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>}

        {!error && !session && <div className="py-10 text-[13px] text-ink-muted">Generating QR code…</div>}

        {!error && session && !expired && (
          <>
            <div className="mb-3.5 inline-block rounded-xl border border-border bg-white p-3">
              <QRCodeSVG value={mobileUrl} size={196} />
            </div>

            <p className="mb-2 text-[12px] leading-relaxed text-ink-muted">
              Scan this with the phone's camera to photograph and upload the vendor invoice directly.
            </p>
            <div className="mb-2 break-all text-[10.5px] text-ink-muted/70">{mobileUrl}</div>

            {usingLocalhost && (
              <div className="mb-2.5 flex gap-2 rounded-lg bg-warn-soft px-3 py-2 text-left text-[11px] text-caution-ink">
                <TriangleAlert size={14} className="mt-px shrink-0" />
                <p className="leading-relaxed">
                  This link points at <b>localhost</b> — a phone can't reach that. Make sure this machine
                  is on the same Wi-Fi as the phone, and that the firewall allows inbound connections on this port.
                </p>
              </div>
            )}

            <div className={'mb-3.5 inline-flex items-center gap-1.5 text-[12px] tabular-nums ' + (runningOut ? 'font-semibold text-caution-ink' : 'text-ink-muted')}>
              <Clock size={12} /> Expires in {mm}:{ss}
            </div>

            {reading && (
              <div className="mb-3.5 flex items-center justify-center gap-2 rounded-lg bg-info-soft px-3 py-2 text-[12px] text-info-ink">
                <span aria-hidden="true" className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {progressMessage(readingSince ? Date.now() - readingSince : 0)}
              </div>
            )}
            {failedMessage && (
              <div className="mb-3.5 rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-caution-ink">{failedMessage}</div>
            )}
            {receivedCount > 0 && !reading && (
              <div className="mb-3.5 flex items-center justify-center gap-2 rounded-lg bg-success-soft px-3 py-2 text-[12px] text-success-ink">
                <CircleCheck size={14} className="shrink-0" />
                {receivedCount} invoice{receivedCount > 1 ? 's' : ''} received
              </div>
            )}
          </>
        )}

        {!error && expired && (
          <div className="mb-3.5 rounded-lg bg-warn-soft px-3 py-3.5 text-[13px] text-caution-ink">
            This QR code has expired.
            {receivedCount > 0 ? ` ${receivedCount} invoice${receivedCount > 1 ? 's were' : ' was'} received before it expired.` : ' Close and generate a new one.'}
          </div>
        )}

        <Button variant="ghost" full onClick={onClose}>{receivedCount > 0 ? 'Done' : 'Close'}</Button>
      </div>
    </div>
  );
}
